// Topbar language switcher (WA4 i18n). Drives the Google Website Translate widget (whole-DOM
// translation, en ⇄ hi/or) via lib/googleTranslate. The control carries `notranslate` so Google never
// rewrites the menu itself. The choice persists (googtrans cookie) and re-applies on every load.
import React, { useEffect } from "react";
import { Menu, MenuItem } from "./ui/Menu";
import { TRANSLATE_LANGS, getActiveLang, applyTranslate, verifyTranslation, ensureWidgetLoaded } from "../lib/googleTranslate";
import { notify as toast } from "./ui/useToast";
import { Globe, Check } from "lucide-react";

export default function LanguageSwitcher() {
  const active = getActiveLang();
  const current = TRANSLATE_LANGS.find((l) => l.code === active) || TRANSLATE_LANGS[0];

  // If a non-English language was chosen but Google's widget can't load (blocked/offline),
  // the page silently stays English — warn the user once instead of failing silently.
  useEffect(() => verifyTranslation(() =>
    toast.error("Translation is unavailable right now — showing English.")), []);

  const choose = (code) => {
    try { applyTranslate(code); }
    catch { toast.error("Couldn't change the language. Please allow cookies and try again."); }
  };
  return (
    // Warm up the (deferred) Google Translate script on the first hover/focus/tap of the switcher —
    // a language change reloads the page anyway, so this is purely a head start, never a dependency.
    <div className="notranslate" translate="no"
      onPointerEnter={ensureWidgetLoaded} onFocus={ensureWidgetLoaded} onTouchStart={ensureWidgetLoaded}>
      <Menu
        align="right"
        aria-label="Language"
        button={
          <button
            className="group relative grid place-items-center w-10 h-10 rounded-xl bg-muted border border-line text-ink-2 hover:bg-accent/10 hover:border-accent/30 hover:text-accent transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            aria-label={`Language: ${current.label}`}
            data-testid="lang-switcher"
          >
            <Globe className="w-[18px] h-[18px] transition-transform duration-200 group-hover:rotate-6" aria-hidden="true" strokeWidth={1.9} />
            <span className="absolute -bottom-0.5 -right-0.5 px-1 rounded-full bg-surface border border-line text-[9px] font-bold uppercase leading-tight text-ink-subtle group-hover:text-accent group-hover:border-accent/30 transition-colors">{current.code}</span>
          </button>
        }
      >
        {TRANSLATE_LANGS.map((l) => (
          <MenuItem key={l.code} onSelect={() => choose(l.code)} icon={l.code === active ? Check : undefined}>
            {l.label}{l.code === active && <span className="sr-only"> (selected)</span>}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
}
